import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Bell, CheckCircle2, Phone, X } from 'lucide-react';
import { socket } from '../admin/chat/socket';
import { getAlertDescription, getAlertLabel } from './alertTypes';

const API = process.env.REACT_APP_API_URL || '';

function severityClasses(severity) {
  if (severity === 'high') {
    return {
      badge: 'bg-red-100 text-red-700',
      border: 'border-red-300',
      icon: 'text-red-600 bg-red-50',
    };
  }
  if (severity === 'low') {
    return {
      badge: 'bg-slate-100 text-slate-700',
      border: 'border-slate-300',
      icon: 'text-slate-600 bg-slate-50',
    };
  }
  return {
    badge: 'bg-amber-100 text-amber-700',
    border: 'border-amber-300',
    icon: 'text-amber-600 bg-amber-50',
  };
}

function formatAlertTime(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    }).format(new Date(value));
  } catch (_) {
    return '';
  }
}

function upsertAlert(alerts, incoming) {
  const active = incoming.status === 'open' || incoming.status === 'acknowledged';
  if (!active) {
    return alerts.filter(alert => alert.id !== incoming.id);
  }

  const exists = alerts.some(alert => alert.id === incoming.id);
  if (exists) {
    return alerts.map(alert => alert.id === incoming.id ? incoming : alert);
  }
  return [incoming, ...alerts];
}

function AlertDetailModal({ alert, onClose, onAction, updating }) {
  if (!alert) return null;

  const delivery = alert.delivery || {};
  const classes = severityClasses(alert.severity);

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 5000 }}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${classes.icon}`}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{getAlertLabel(alert.alert_type)}</h2>
              <p className="text-xs text-slate-500">{formatAlertTime(alert.created_at)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex flex-wrap gap-2">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${classes.badge}`}>
              {alert.severity === 'high' ? 'Alta' : alert.severity === 'low' ? 'Baja' : 'Media'}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
              {alert.status === 'acknowledged' ? 'En atencion' : 'Nueva'}
            </span>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <p className="text-sm text-slate-700">{getAlertDescription(alert.alert_type)}</p>
            {alert.message && (
              <p className="mt-3 text-sm text-slate-900 font-medium">{alert.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-slate-500">Entrega</div>
              <div className="font-semibold text-slate-900">#{delivery.id || alert.delivery_point_id}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Tienda</div>
              <div className="font-semibold text-slate-900">{delivery.user_name || alert.user?.username || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Cliente final</div>
              <div className="font-semibold text-slate-900">{delivery.recipient_name || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Conductor</div>
              <div className="font-semibold text-slate-900">{alert.driver?.username || '-'}</div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs text-slate-500">Direccion</div>
            <div className="text-sm font-medium text-slate-900 mt-1">
              {[delivery.address, delivery.district].filter(Boolean).join(', ') || '-'}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {delivery.recipient_phone && (
              <a
                href={`tel:${delivery.recipient_phone}`}
                className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Phone className="w-4 h-4" />
                Cliente
              </a>
            )}
            {alert.driver?.phone_number && (
              <a
                href={`tel:${alert.driver.phone_number}`}
                className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Phone className="w-4 h-4" />
                Conductor
              </a>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 p-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            onClick={() => onAction(alert, 'acknowledge')}
            disabled={updating || alert.status === 'acknowledged'}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Atender
          </button>
          <button
            onClick={() => onAction(alert, 'dismiss')}
            disabled={updating}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Descartar
          </button>
          <button
            onClick={() => onAction(alert, 'resolve')}
            disabled={updating}
            className="rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary/90 disabled:opacity-50"
          >
            Resolver
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DeliveryAlertsCenter({ role, userId, providerId }) {
  const [alerts, setAlerts] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [updating, setUpdating] = useState(false);

  const shouldListen = role === 'admin' || role === 'provider' || role === 'customer';
  const normalizedProviderId = providerId && providerId !== 'null' && providerId !== 'undefined'
    ? providerId
    : null;

  const joinPayload = useMemo(() => ({
    role,
    user_id: userId,
    provider_id: normalizedProviderId,
  }), [role, userId, normalizedProviderId]);

  const joinRooms = useCallback(() => {
    if (!shouldListen || !userId) return;
    socket.emit('join_delivery_alerts', joinPayload);
  }, [joinPayload, shouldListen, userId]);

  useEffect(() => {
    if (!shouldListen || !userId) return;

    const fetchAlerts = async () => {
      try {
        const response = await axios.get(`${API}/api/delivery-alerts/open`, {
          params: { role, user_id: userId },
        });
        setAlerts(response.data.alerts || []);
      } catch (error) {
        console.error('Error loading delivery alerts:', error);
      }
    };

    fetchAlerts();
    const pollId = window.setInterval(fetchAlerts, 15000);
    joinRooms();

    const handleCreated = (alert) => {
      setAlerts(prev => upsertAlert(prev, alert));
      setPanelOpen(true);
    };
    const handleUpdated = (alert) => {
      setAlerts(prev => upsertAlert(prev, alert));
      setSelectedAlert(prev => prev?.id === alert.id ? alert : prev);
    };
    const handleConnect = () => joinRooms();

    socket.on('delivery_alert_created', handleCreated);
    socket.on('delivery_alert_updated', handleUpdated);
    socket.on('connect', handleConnect);

    return () => {
      socket.off('delivery_alert_created', handleCreated);
      socket.off('delivery_alert_updated', handleUpdated);
      socket.off('connect', handleConnect);
      socket.emit('leave_delivery_alerts', joinPayload);
      window.clearInterval(pollId);
    };
  }, [joinPayload, joinRooms, role, shouldListen, userId]);

  const handleAction = async (alertItem, action) => {
    try {
      setUpdating(true);
      const response = await axios.patch(`${API}/api/delivery-alerts/${alertItem.id}`, {
        action,
        user_id: userId,
      });
      const updated = response.data.alert;
      setAlerts(prev => upsertAlert(prev, updated));
      if (action === 'dismiss' || action === 'resolve') {
        setSelectedAlert(null);
      } else {
        setSelectedAlert(updated);
      }
    } catch (error) {
      console.error('Error updating delivery alert:', error);
      alert('No se pudo actualizar la alerta.');
    } finally {
      setUpdating(false);
    }
  };

  if (!shouldListen || !userId) return null;

  const visibleAlerts = alerts.slice(0, panelOpen ? 8 : 3);

  return (
    <>
      <div className="fixed right-4 bottom-4 z-[4000] w-[calc(100vw-2rem)] max-w-sm pointer-events-none">
        <div className="flex flex-col items-end gap-3">
          {panelOpen && visibleAlerts.length > 0 && (
            <div className="w-full space-y-2 pointer-events-auto">
              {visibleAlerts.map((item) => {
                const classes = severityClasses(item.severity);
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedAlert(item)}
                    className={`w-full text-left rounded-xl border ${classes.border} bg-white shadow-xl p-3 hover:bg-slate-50 transition-colors`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${classes.icon}`}>
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-bold text-sm text-slate-900 truncate">
                            {getAlertLabel(item.alert_type)}
                          </div>
                          <div className="text-[11px] text-slate-500 flex-shrink-0">
                            {formatAlertTime(item.created_at)}
                          </div>
                        </div>
                        <div className="text-xs text-slate-600 mt-1 truncate">
                          #{item.delivery_point_id} - {item.delivery?.recipient_name || 'Entrega'}
                        </div>
                        {item.message && (
                          <div className="text-xs text-slate-800 mt-1 truncate">{item.message}</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <button
            onClick={() => setPanelOpen(open => !open)}
            className="pointer-events-auto relative w-14 h-14 rounded-full bg-slate-900 text-white shadow-2xl flex items-center justify-center hover:bg-slate-800"
            title="Alertas de entrega"
          >
            {alerts.length > 0 ? <AlertTriangle className="w-6 h-6" /> : <Bell className="w-6 h-6" />}
            {alerts.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-6 h-6 px-1 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                {alerts.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <AlertDetailModal
        alert={selectedAlert}
        onClose={() => setSelectedAlert(null)}
        onAction={handleAction}
        updating={updating}
      />

      {panelOpen && alerts.length === 0 && (
        <div className="fixed right-4 bottom-20 z-[4000] rounded-xl bg-white shadow-xl border border-slate-200 p-4 text-sm text-slate-600 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-brand-green" />
          No hay alertas activas.
        </div>
      )}
    </>
  );
}
